import { useEffect, useState } from 'react';

import { SearchBar, SearchWithDropdown } from '@zendesk/forethought-elements';
import { useGetSearchQuery } from '../dashboard-pages/solve-insights/hooks/useGetSearchQuery';
import WithHelperText from 'src/components/with-helper-text/WithHelperText';

interface DebouncedSearchBarBaseProps {
  ariaLabel?: string;
  // Extra side effect after the input is cleared via its clear button
  // (e.g. resetting table pagination).
  onCleared?: () => void;
  // Called with the debounced, committed query — never per keystroke.
  onCommit: (query: string) => void;
  placeholder?: string;
  // The committed query, i.e. the value that actually drives the table
  // request. Keystroke-level text is local state below.
  query: string;
  size?: 'large' | 'small';
  // Optional live validation of the transient text; the returned message
  // renders under the input while typing.
  validate?: (uiQuery: string) => string;
}

type DebouncedSearchBarProps = DebouncedSearchBarBaseProps &
  (
    | {
        onModeChange?: (mode: string) => void;
        options: { text: string; value: string }[];
        selectedMode: string;
      }
    | {
        onModeChange?: never;
        options?: undefined;
        selectedMode?: never;
      }
  );

/**
 * Search input for table toolbars with keystroke-level state isolation.
 *
 * The transient text state is deliberately confined to this leaf: each
 * keystroke re-renders only this small subtree, and the surrounding table
 * (which is expensive to re-render at hundreds of rows) only updates when
 * the debounced query commits via `onCommit`. External writes to `query`
 * (reset filters, filter drawer, browser back/forward) sync back into the
 * input; self-commits are no-ops because they arrive with query === uiQuery.
 */
const DebouncedSearchBar = ({
  ariaLabel,
  onCleared,
  onCommit,
  onModeChange,
  options,
  placeholder = '',
  query,
  selectedMode,
  size,
  validate,
}: DebouncedSearchBarProps) => {
  const { setUiQuery, uiQuery } = useGetSearchQuery({
    searchQuery: query,
    setSearchQuery: onCommit,
  });

  const [syncedQuery, setSyncedQuery] = useState(query);
  useEffect(() => {
    if (query !== syncedQuery) {
      setSyncedQuery(query);
      setUiQuery(query);
    }
  }, [query, syncedQuery, setUiQuery]);

  const handleClear = () => {
    setUiQuery('');
    onCleared?.();
  };

  const handleModeChange = (mode: string) => {
    onModeChange?.(mode);
    setUiQuery('');
  };

  if (options) {
    const dropdown = (
      <SearchWithDropdown
        aria-label={ariaLabel}
        fullWidth
        onChange={e => setUiQuery(e.target.value)}
        onClear={handleClear}
        options={[...options]}
        placeholder={placeholder}
        selectChange={handleModeChange}
        selectedOption={selectedMode}
        value={uiQuery}
      />
    );

    return validate ? (
      <WithHelperText helperText={validate(uiQuery)}>{dropdown}</WithHelperText>
    ) : (
      dropdown
    );
  }

  return (
    <SearchBar
      aria-label={ariaLabel}
      onChange={e => setUiQuery(e.target.value)}
      placeholder={placeholder}
      size={size}
      value={uiQuery}
    />
  );
};

export default DebouncedSearchBar;
